import React, { useState } from 'react';

import './CommunityTopic.scss';
import { timeSince } from '../../../helpers/date';
import { getString } from '../../../helpers/styles';
import Transition from '../../Transition/Transition';
import icons from '../../../shared/assets/icons/redesign';
import HTMLContent from '../../HTMLContent/HTMLContent.jsx';
import CommunityNewTopicEventPopupContent from '../../redesign/PopupContent/CommunityNewTopicEventPopupContent/CommunityNewTopicEventPopupContent.jsx';
import CommunityNewTopicEventReactionPopupContent from '../../redesign/PopupContent/CommunityNewTopicEventReactionPopupContent/CommunityNewTopicEventReactionPopupContent.jsx';
import CommunityFilterTopicEventsPopupContent from '../../redesign/PopupContent/CommunityFilterTopicEventsPopupContent/CommunityFilterTopicEventsPopupContent';

const sortTypes = [
  'Nieuwste boven',
  'Oudste boven',
  'Meeste likes',
  'Minste likes'
];

const { Flag, Like, Filter, Reply, ChevronDown, ChevronUp } = icons;

export default function CommunityTopic({
  user,
  topic,
  addTopicEvent,
  addTopicEventReaction,
  removeTopicEventReaction,
  addTopicModerationReview,
  addTopicEventModerationReview
}) {
  const [filterPopup, showFilterPopup] = useState(false);
  const [newTopicEventPopup, showNewTopicEventPopup] = useState(true);
  const [newTopicEventReactionPopup, showNewTopicEventReactionPopup] =
    useState(null);
  const [sortType, setSortType] = useState(sortTypes[0]);

  return (
    <div className="componentWrap">
      <div className="communityTopic">
        <AuthorCard
          authorName={topic.createdBy.displayName}
          authorImg={topic.createdBy.profilePicture}
          updateTime={timeSince(new Date(topic.createdAt))}
        />

        <div className="communityTopic-content">
          <h3 className="communityTopic-subject">{topic.subject}</h3>
          <pre className="communityTopic-message">{topic.description}</pre>
        </div>

        <ActionBar
          user={user}
          topic={topic}
          addTopicModerationReview={addTopicModerationReview}
          onReply={() => (
            showNewTopicEventPopup(true), showNewTopicEventReactionPopup(null)
          )}
        />

        <div className="communityTopic-events">
          <div className="communityTopic-events-title">
            {topic.events.length} Reacties
          </div>

          <Filter
            className="cursor-pointer"
            onClick={() => showFilterPopup(true)}
          />
        </div>

        <ul className="communityTopic-events-content">
          {sortList(sortType).map((event, idx) => {
            const isReaction = event.message.includes('&lt;blockquote&gt;');

            return (
              <li key={idx}>
                <AuthorCard
                  authorName={event?.createdBy?.displayName}
                  authorImg={event?.createdBy?.profilePicture}
                  updateTime={timeSince(new Date(event.createdAt))}
                />

                {isReaction ? (
                  <ReactionMessage event={event} />
                ) : (
                  <HTMLContent
                    content={event.message}
                    className="communityTopic-event-message"
                  />
                )}

                <ActionBar
                  user={user}
                  event={event}
                  isReaction={isReaction}
                  addTopicEventReaction={addTopicEventReaction}
                  removeTopicEventReaction={removeTopicEventReaction}
                  addTopicEventModerationReview={addTopicEventModerationReview}
                  onReply={() => (
                    showNewTopicEventReactionPopup(event.message),
                    showNewTopicEventPopup(false)
                  )}
                />
              </li>
            );
          })}
        </ul>
      </div>

      <Transition on={newTopicEventPopup}>
        <div className="communityTopicList-new-topic-event-popup">
          <CommunityNewTopicEventPopupContent
            topicXuid={topic.xuid}
            addTopicEvent={addTopicEvent}
            onClose={() => showNewTopicEventPopup(false)}
          />
        </div>
      </Transition>

      <Transition on={newTopicEventReactionPopup}>
        <div className="communityTopicList-new-topic-event-popup">
          <CommunityNewTopicEventReactionPopupContent
            topicXuid={topic.xuid}
            addTopicEvent={addTopicEvent}
            newTopicEventReactionPopup={newTopicEventReactionPopup}
            onClose={() => showNewTopicEventReactionPopup(null)}
          />
        </div>
      </Transition>

      <Transition on={filterPopup}>
        <div className="communityTopicList-filter-popup">
          <CommunityFilterTopicEventsPopupContent
            sortType={sortType}
            sortTypes={sortTypes}
            setSortType={setSortType}
            onClose={() => showFilterPopup(false)}
          />
        </div>
      </Transition>
    </div>
  );

  function sortList(type) {
    switch (type) {
      case sortTypes[0]: {
        return [...topic.events].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      }

      case sortTypes[1]:
        return [...topic.events].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );

      case sortTypes[2]: {
        return [...topic.events].sort(
          (a, b) => b.reactions.length - a.reactions.length
        );
      }

      case sortTypes[3]: {
        return [...topic.events].sort(
          (a, b) => a.reactions.length - b.reactions.length
        );
      }
    }
  }
}

function AuthorCard({ authorImg, authorName, updateTime }) {
  return (
    <div className="communityTopic-author">
      <img
        className="communityTopic-author-img"
        src={authorImg}
        alt="author-image"
      />

      <div>
        <p className="communityTopic-author-name">{authorName}</p>
        <span className="communityTopic-update-time">{updateTime}</span>
      </div>
    </div>
  );
}

function ReactionMessage({ event }) {
  const [expandedTopicEvents, setExpandedTopicEvents] = useState([]);
  const [parentMessage, message] = event.message
    .slice('<p>&lt;blockquote&gt;&lt;p&gt;'.length)
    .split('&lt;/p&gt;&lt;/blockquote&gt;');

  const isLong = parentMessage.length > 70;
  const isExpanded = expandedTopicEvents.includes(event.xuid);

  return (
    <div>
      <div
        className="communityTopic-event-quoted-message-wrap"
        onClick={() =>
          isLong &&
          setExpandedTopicEvents(
            isExpanded
              ? expandedTopicEvents.filter(({ xuid }) => xuid === event.xuid)
              : [...expandedTopicEvents, event.xuid]
          )
        }
      >
        <HTMLContent
          content={
            isLong
              ? isExpanded
                ? parentMessage
                : `${parentMessage.slice(0, 70)}...`
              : parentMessage
          }
          className="communityTopic-event-quoted-message"
        />

        {isLong && (
          <div>
            {isExpanded ? (
              <ChevronUp className="communityTopic-event-quoted-message-icon" />
            ) : (
              <ChevronDown className="communityTopic-event-quoted-message-icon" />
            )}
          </div>
        )}
      </div>

      <HTMLContent className="communityTopic-event-message" content={message} />
    </div>
  );
}

function ActionBar({
  user,
  event,
  topic,
  onReply,
  isReaction,
  addTopicEventReaction,
  removeTopicEventReaction,
  addTopicModerationReview,
  addTopicEventModerationReview
}) {
  const isLiked = event?.reactions.find(
    (reaction) => reaction.username === user.username
  );

  const [isUpdating, setIsUpdating] = useState(false);

  return (
    <div className="communityTopic-action-bar">
      <Flag
        className={getString([
          {
            value: 'communityTopic-action-icon cursor-pointer',
            condition: true
          },
          {
            value: 'communityTopic-action-icon-marked',
            condition:
              (addTopicModerationReview && topic.isModerationFlagged) ||
              (addTopicEventModerationReview && event.isModerationFlagged)
          }
        ])}
        onClick={() => {
          if (addTopicEventModerationReview && !event.isModerationFlagged)
            addTopicEventModerationReview({
              variables: {
                topicEventXuid: event.xuid,
                reason: 'FLAGGED'
              }
            });
          else if (addTopicModerationReview && !topic.isModerationFlagged)
            addTopicModerationReview({
              variables: {
                topicXuid: topic.xuid,
                reason: 'FLAGGED'
              }
            });
        }}
      />

      <div className="communityTopic-action-bar-section">
        {addTopicEventReaction && (
          <>
            <Like
              className={getString([
                {
                  value: 'communityTopic-action-icon cursor-pointer',
                  condition: true
                },
                {
                  value: 'communityTopic-action-icon-marked',
                  condition: event?.reactions.find(
                    ({ username }) => username === user.username
                  )
                }
              ])}
              onClick={() => {
                if (isUpdating) return;

                setIsUpdating(true);

                if (isLiked)
                  removeTopicEventReaction({
                    variables: { topicEventXuid: event.xuid }
                  }).then(() => setIsUpdating(false));
                else
                  addTopicEventReaction({
                    variables: {
                      topicEventXuid: event.xuid,
                      reaction: 'LIKE'
                    }
                  }).then(() => setIsUpdating(false));
              }}
            />

            <span className="communityTopic-like-count">
              {event.reactions.length}
            </span>
          </>
        )}

        {!isReaction && (
          <Reply
            className="cursor-pointer communityTopic-reply-icon"
            onClick={() => onReply()}
          />
        )}
      </div>
    </div>
  );
}
